import React, { useEffect, useState } from 'react'

// next components
import { useRouter } from 'next/router'

// third party components
import { useWallet } from '@solana/wallet-adapter-react'
import { useDispatch, useSelector } from 'react-redux'
import { useWalletModal } from '@solana/wallet-adapter-react-ui'

// custom components
import AlertMobile from 'components/AlertMobile'
import AccordionMenu from 'components/Menu/AccordionMenu'
import MyWalletSubMenu from 'components/Menu/MyWalletSubMenu'

// images and icons
import DarkLightModeIcon from 'assets/icons/dark-light-mode-mobile.svg?component'
import LightDarkModeIcon from 'assets/icons/light-dark-mode-mobile.svg?component'
import BackIcon from 'assets/icons/back-arrow.svg?component'

// json data
import ProfileMenuList from 'assets/json/profile-menu-mobile.json'
import ExploreMenuList from 'assets/json/explore-mobile-menu.json'
import ShopMenuList from 'assets/json/shop-menu.json'
import CreateMenuList from 'assets/json/create-menu.json'

// styles
import styles from './index.module.scss'

// call apis
import { setColorTheme } from 'utils/helpers'
import { deleteCookie } from 'cookies-next'
import { useAuthentication } from 'hooks/useAuthentication'
import { getDisplayUserName } from 'utils/strings'
import MobileMenuItem from '../MobileMenuItem'
import MobileMenuUser from '../MobileMenuUser'
import ResourcesMenu from '../ResourcesMenu'
import GradientButton from 'components/Button/GradientButton'

const MobileMenu = () => {
  const dispatch = useDispatch()
  const router = useRouter()

  const { connected, disconnect } = useWallet()
  const [lightMode, setLightMode] = useState(useSelector(state => (state as any).lightMode))
  const { setVisible } = useWalletModal()

  const [showAlert, setShowAlert] = useState(false)
  const [alertData, setAlertData] = useState<any[]>([])
  const [buttonClicked, setButtonClicked] = useState(false)

  const { profileInfo } = useAuthentication()
  const [profileImage, setProfileImage] = useState(null)
  const [profileName, setProfileName] = useState('')
  const [menuType, setMenuType] = useState('')

  // update profile menu list
  const [profileMenuList, setProfileMenuList] = useState(ProfileMenuList)
  useEffect(() => {
    if (profileInfo !== null) {
      let array = [...profileMenuList]
      array.forEach(item => {
        if (item.label === 'Profile' && item.path === '/') {
          item.path += profileInfo.username.replaceAll(' ', '%20')
        } else if (item.label === 'My Store' && item.path === '/') {
          item.path += profileInfo.username.replaceAll(' ', '%20') + '/merch'
        }
      })
      setProfileMenuList(array)

      if (profileInfo.username) {
        setProfileName(profileInfo.username)
      }
      setProfileImage(profileInfo.profile_picture)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileInfo])

  useEffect(() => {
    if (!buttonClicked) {
      return
    }

    if (connected) {
      setButtonClicked(false)
      // setAlertData([
      //   {
      //     id: 0,
      //     title: 'Wallet successfully connected!',
      //     description: `You've successfully disconnected your wallet!`,
      //     type: 'success',
      //   },
      // ])
      // setShowAlert(true)
    } else {
      setButtonClicked(true)

      setAlertData([
        {
          id: 0,
          title: 'Wallet successfully disconnected!',
          description: `You've successfully disconnected your wallet!`,
          type: 'success',
        },
      ])
      setShowAlert(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected])

  const handleThemeMode = () => {
    if (lightMode) {
      localStorage.setItem('lightDarkMode', 'false')
    } else {
      localStorage.setItem('lightDarkMode', 'true')
    }
    setColorTheme(!lightMode)
    dispatch({ type: 'set', lightMode: !lightMode })
    setLightMode(!lightMode)
  }

  const logout = () => {
    setButtonClicked(true)
    if (localStorage.getItem('walletName') === '"Phantom"') {
      localStorage.removeItem('walletName')
      ;(window as any).solana.disconnect()
    } else disconnect()

    localStorage.removeItem('token')
    dispatch({ type: 'set', profileInfo: null })
    localStorage.removeItem('expiryDate')
    localStorage.removeItem('signature')
    deleteCookie('token')
  }

  const [currentView, setCurrentView] = useState<'Main' | 'Detail'>('Main')

  const gotoDetailView = type => {
    setMenuType(type)
    setCurrentView('Detail')
  }

  const gotoMainView = () => {
    setCurrentView('Main')
  }

  const getMenuDetailTitle = () => {
    switch (menuType) {
      case 'create':
        return 'Create'
      case 'explore':
        return 'Explore'
      case 'shop':
        return 'Shop'
      case 'wallet':
        return 'My Wallets'
      case 'resources':
        return 'Resources'
    }
  }

  return (
    <div className={styles.menu}>
      <div className={`${styles.viewContainer} ${currentView === 'Main' ? styles.view1Active : styles.view2Active}`}>
        <div className={styles.view}>
          <div className="w-full h-full overflow-auto">
            {connected && profileInfo?.username?.length > 0 && (
              <MobileMenuUser icon={profileImage} username={getDisplayUserName(profileName)} />
            )}

            <div className={styles.container}>
              <MobileMenuItem
                type="create"
                onClick={() => {
                  gotoDetailView('create')
                }}
              />

              <MobileMenuItem
                type="explore"
                onClick={() => {
                  gotoDetailView('explore')
                }}
              />

              <MobileMenuItem
                type="shop"
                onClick={() => {
                  gotoDetailView('shop')
                }}
              />
              {connected && profileInfo?.username?.length > 0 && (
                <MobileMenuItem
                  type="wallet"
                  onClick={() => {
                    gotoDetailView('wallet')
                  }}
                />
              )}

              <MobileMenuItem
                type="resources"
                onClick={() => {
                  gotoDetailView('resources')
                }}
              />

              <div className="w-full flex items-center gap-[15px] mt-[30px]" onClick={() => handleThemeMode()}>
                <button>{lightMode ? <LightDarkModeIcon /> : <DarkLightModeIcon />}</button>
                <div className={styles.colorMode}>{lightMode ? 'Light Mode' : 'Dark Mode'}</div>
              </div>

              {connected && profileInfo?.username?.length > 0 ? (
                <div className="w-full flex flex-wrap mt-[15px]">
                  <GradientButton
                    fullWidth
                    fontFamily="Jost-Regular"
                    fontSize={16}
                    label="Logout"
                    onClick={() => {
                      logout()
                    }}
                  />
                </div>
              ) : profileInfo === null ? (
                <div className="w-full h-[53px] flex justify-between items-center  mt-[15px]">
                  <GradientButton
                    fullWidth
                    label="Login"
                    onClick={() => {
                      setButtonClicked(true)
                      setVisible(true)
                    }}
                  />

                  <AlertMobile
                    open={showAlert}
                    close={() => {
                      router.push('/')
                      setShowAlert(false)
                    }}
                    data={alertData}
                  />
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>

        <div className={`${styles.view} px-[16px]`}>
          {menuType.length > 0 && (
            <div className="w-full flex gap-[15px] items-center" onClick={() => gotoMainView()}>
              <div className="w-[24px] h-[36px] flex justify-start items-center">
                <BackIcon />
              </div>
              <div className={styles.menuDetailTitle}>{getMenuDetailTitle()}</div>
            </div>
          )}

          {menuType === 'create' && (
            <AccordionMenu type={menuType} data={CreateMenuList} username={profileInfo?.username} />
          )}

          {menuType === 'explore' && (
            <AccordionMenu type={menuType} data={ExploreMenuList} username={profileInfo?.username} />
          )}

          {menuType === 'shop' && (
            <AccordionMenu type={menuType} data={ShopMenuList} username={profileInfo?.username} />
          )}

          {menuType === 'wallet' && <MyWalletSubMenu label="Wallet" type="" username={profileInfo?.username} />}

          {menuType === 'resources' && <ResourcesMenu />}
        </div>
      </div>
    </div>
  )
}

export default MobileMenu
